/**
 * Operator-comms content: the words the operator channel can say, plus the
 * beat metadata (priority / speaker / minor) that classifies each line for the
 * arbiter. This module is pure data — no behavior, no DOM, no timers. The
 * arbitration mechanism lives in BeatScheduler.ts; the event→beat mapping and
 * game-state bookkeeping live in CommsDirector.ts.
 *
 * BL-89 — the corpus is i18n-keyed. Each `VERA_*`/`FENRIR_*` array holds
 * **i18n keys**, not raw English; the director keeps picking a key the same way
 * (same render-side RNG, same order) and `commsLine(key)` resolves it to the
 * active locale at the display point. EN is the canonical fallback (so a raw
 * key can never render, mirroring the BL-113 data seam); DE is idiomatic,
 * voice-preserving German. EN+DE travel together here so the bark and its
 * translation can be spot-checked side by side; `translations.ts` registers the
 * `comms.*` family into the i18n seam.
 */

export type BeatPriority = 0 | 1 | 2 | 3 | 4;
/** "echelon" is the S130 SIGNAL OVERRIDE (Story Bible §6.3) — a scripted-track
 *  speaker only; she has no reactive bark pools. */
export type Speaker = "vera" | "fenrir" | "echelon";

export interface BeatSpec {
  id: string;
  priority: BeatPriority;
  minor: boolean;
  speaker: Speaker;
}

export const PRIORITY = {
  AMBIENT: 0 as BeatPriority,
  LOW: 1 as BeatPriority,
  MED: 2 as BeatPriority,
  HIGH: 3 as BeatPriority,
  MAX: 4 as BeatPriority,
};

// ─── i18n-keyed corpus (BL-89) ───────────────────────────────────────────────
//
// `bark(prefix, lines)` turns an EN/DE corpus into an array of keys
// `${prefix}.${i}` and records both locales. The returned key array is what the
// director picks from (the pick path / RNG is unchanged — keys substitute 1:1
// for the old raw strings); the EN/DE maps feed the i18n seam + the fallback.

const COMMS_EN: Record<string, string> = {};
const COMMS_DE: Record<string, string> = {};

function bark(
  prefix: string,
  lines: ReadonlyArray<readonly [en: string, de: string]>,
): readonly string[] {
  return lines.map(([en, de], i) => {
    const key = `${prefix}.${i}`;
    COMMS_EN[key] = en;
    COMMS_DE[key] = de;
    return key;
  });
}

// S143 / BL-139 (PT-40 #4): added 3-5 variants per pool below, voice-preserving
// (VERA warm/terse, FENRIR cold/taunting), width-aware (UPPERCASE, the existing
// "terminal voice" lines stay short enough for the commentary TV at every
// `--ui-scale`). EN canonical + DE idiomatic. The director's pick path + the
// "no back-to-back duplicate" rule are unchanged — the larger pools simply give
// it more material per existing trigger.
export const VERA_RUN_START = bark("comms.vera.runStart", [
  ["COMMS ONLINE.", "FUNK ONLINE."],
  ["LINK STABLE.", "LINK STABIL."],
  ["READY WHEN YOU ARE.", "BEREIT, WENN DU ES BIST."],
  ["GRID FEED CLEAN.", "GRID-FEED SAUBER."],
  ["OPS CHANNEL LIVE.", "OPS-KANAL LIVE."],
  ["I'M HERE. LET'S WORK.", "ICH BIN DA. LASS UNS ARBEITEN."],
  ["UPLINK GOOD. EYES UP.", "UPLINK GUT. AUGEN AUF."],
  ["TERMINAL WARM. GO.", "TERMINAL WARM. LOS."],
  ["GRID IS YOURS.", "DAS GRID GEHÖRT DIR."],
]);

export const VERA_STEALTH_FIRST = bark("comms.vera.stealthFirst", [
  ["WOAH. CLOAKED SIGS?!", "WOAH. GETARNTE SIGS?!"],
  ["STEALTH CONTACT.", "STEALTH-KONTAKT."],
  ["GHOST TRAFFIC?!", "GEISTER-TRAFFIC?!"],
  ["EYES — THEY'RE INVISIBLE.", "AUGEN AUF — DIE SIND UNSICHTBAR."],
  ["SHE SENT GHOSTS.", "SIE SCHICKT GEISTER."],
  ["CONTACTS UNDER THE RADAR.", "KONTAKTE UNTERM RADAR."],
]);

export const VERA_STEALTH_REPEAT = bark("comms.vera.stealthRepeat", [
  ["GHOSTS AGAIN.", "WIEDER GEISTER."],
  ["STEALTH AGAIN. FINE.", "WIEDER STEALTH. GUT."],
  ["CLOAKS BACK ONLINE.", "TARNUNG WIEDER ONLINE."],
  ["MORE OF THE INVISIBLE.", "MEHR VON DEN UNSICHTBAREN."],
  ["KNOW THE DRILL — GHOSTS.", "DU KENNST ES — GEISTER."],
  ["CLOAKS UP. EYES SHARPER.", "TARNUNG OBEN. AUGEN SCHÄRFER."],
  // S193 (BL-271): the hottest pool in a full run (measured ~12 fires) —
  // grown to match its fire-frequency. Append-only.
  ["CLOAKS ON THE FEED AGAIN.", "WIEDER TARNKAPPEN IM FEED."],
  ["THEY LIKE THE DARK. WATCH IT.", "SIE MÖGEN DAS DUNKEL. PASS AUF."],
  ["GHOST PATTERN REPEATS.", "GEISTER-MUSTER WIEDERHOLT SICH."],
  ["INVISIBLE TRAFFIC INBOUND.", "UNSICHTBARER TRAFFIC IM ANFLUG."],
]);

// First time the core takes a hit this run — wary "that was close", not alarm.
export const VERA_FIRST_LEAK = bark("comms.vera.firstLeak", [
  ["FIRST LEAK. TIGHTEN UP.", "ERSTES LECK. MACH DICHT."],
  ["ONE GOT THROUGH. NOTED.", "EINER DURCH. NOTIERT."],
  ["THAT STUNG. STAY SHARP.", "DAS HAT GEBRANNT. BLEIB WACH."],
  ["FIRST CRACK. NO MORE.", "ERSTER RISS. KEINER MEHR."],
  ["WE FELT THAT. CLOSE IT.", "DAS WAR SPÜRBAR. SCHLIESS ES."],
  ["LEAK ONE — DON'T MAKE A HABIT.", "LECK EINS — KEINE GEWOHNHEIT WERDEN."],
]);

// A tower was lost to a breach — wary call to close the gap, not panic.
export const VERA_NODE_LOST = bark("comms.vera.nodeLost", [
  ["WE LOST A NODE.", "EIN KNOTEN IST WEG."],
  ["DEFENSE GAP — PATCH IT.", "ABWEHRLÜCKE — STOPF SIE."],
  ["NODE DOWN. REBUILD IT.", "KNOTEN AUSGEFALLEN. BAU NEU."],
  ["TOWER OFFLINE. RE-DEPLOY.", "TURM OFFLINE. NEU AUFSTELLEN."],
  ["GRID THIN HERE — STAFF IT.", "GRID DÜNN HIER — BESETZ ES."],
  ["A SLOT JUST OPENED. CLOSE IT.", "EIN SLOT IST FREI. SCHLIESS IHN."],
]);

// First sighting of a new enemy type this run — wary "haven't seen this one".
export const VERA_NEW_SIGNATURE = bark("comms.vera.newSignature", [
  ["NEW SIGNATURE. UNKNOWN.", "NEUE SIGNATUR. UNBEKANNT."],
  ["HAVEN'T SEEN THIS ONE.", "DIE KENN ICH NICHT."],
  ["UNFAMILIAR CONTACT.", "UNBEKANNTER KONTAKT."],
  ["NEW ENTRY IN THE LOG.", "NEUER EINTRAG IM LOG."],
  ["FRESH TYPE — STUDY IT.", "NEUER TYP — STUDIER IHN."],
  ["FIRST CONTACT. WATCH WEAKNESS.", "ERSTKONTAKT. SCHWÄCHE IM BLICK."],
  // S193 (BL-271): fires once per new archetype (~8/run) — grown to match.
  ["ODD READOUT. LOGGING IT.", "SELTSAME WERTE. WIRD GELOGGT."],
  ["THAT ONE'S NEW. EYES ON.", "DER IST NEU. IM BLICK BEHALTEN."],
  ["UNKNOWN BUILD. CAREFUL.", "UNBEKANNTER BAU. VORSICHT."],
]);

export const FENRIR_GLOAT = bark("comms.fenrir.gloat", [
  ["YOUR CORE BLEEDS.", "DEIN KERN BLUTET."],
  ["YOU FEEL THAT?", "SPÜRST DU DAS?"],
  ["I OWN THIS LANE.", "DIESE BAHN GEHÖRT MIR."],
  ["THAT CORE IS MINE.", "DIESER KERN IST MEIN."],
  ["I BREAK WHAT YOU LOVE.", "ICH BRECHE, WAS DU LIEBST."],
  ["YOU CAN'T HOLD THIS.", "DU HÄLTST DAS NICHT."],
  ["YOUR WALL IS PAPER.", "DEINE MAUER IST PAPIER."],
  ["EVERY GAP IS A DOOR.", "JEDE LÜCKE IST EINE TÜR."],
  ["BREATHE. WHILE YOU CAN.", "ATME. SOLANGE DU KANNST."],
  ["I CAN HEAR YOUR GRID.", "ICH HÖRE DEIN GRID."],
]);

export const VERA_CORE_HIT = bark("comms.vera.coreHit", [
  ["...THAT ONE HURT.", "...DAS TAT WEH."],
  ["CORE TOOK A HIT.", "KERN GETROFFEN."],
  ["WE CAN STILL STABILIZE.", "WIR STABILISIEREN NOCH."],
  ["DAMAGE REGISTERED.", "SCHADEN ERFASST."],
  ["PATCHING, HOLD.", "PATCHE, HALT DURCH."],
  ["WE BEND, NOT BREAK.", "WIR BIEGEN UNS, BRECHEN NICHT."],
  ["KEEP YOUR HANDS STEADY.", "RUHIGE HÄNDE."],
  ["ONE THROUGH. NOT TWO.", "EINER DURCH. NICHT ZWEI."],
  ["MAINFRAME STILL READS GREEN.", "MAINFRAME ZEIGT NOCH GRÜN."],
]);

// Heavy single core hit (multi-life damage) — VERA visibly sad/frustrated.
// Distinct from the neutral VERA_CORE_HIT response to ordinary 1-life leaks.
export const VERA_HEAVY_HIT = bark("comms.vera.heavyHit", [
  ["THAT ONE REALLY HURT.", "DAS TAT WIRKLICH WEH."],
  ["BIG HIT — I FELT THAT.", "HARTER TREFFER — DAS HAB ICH GESPÜRT."],
  ["WE'RE TAKING REAL DAMAGE.", "WIR NEHMEN ECHTEN SCHADEN."],
  ["THAT TORE RIGHT THROUGH US.", "DAS RISS GLATT DURCH UNS."],
  ["DEEP HIT. THIS STINGS.", "TIEFER TREFFER. DAS BRENNT."],
  ["BUFFER DOWN HARD.", "PUFFER HART GETROFFEN."],
  ["THAT WAS MULTI-LIFE.", "DAS WAR MEHRFACH-SCHADEN."],
  ["WE NEED TO COMPOSE.", "WIR MÜSSEN UNS SAMMELN."],
]);

export const VERA_CRITICAL = bark("comms.vera.critical", [
  ["CORE CRITICAL!", "KERN KRITISCH!"],
  ["WE'RE ON THE EDGE!", "WIR SIND AM LIMIT!"],
  ["LAST CHANCES. HOLD!", "LETZTE CHANCEN. HALTEN!"],
  ["ONE MORE AND WE'RE OUT.", "EINER NOCH UND WIR SIND RAUS."],
  ["RED LINE. HOLD!", "ROTE LINIE. HALTEN!"],
  ["BREATHE — HOLD — BREATHE.", "ATMEN — HALTEN — ATMEN."],
]);

export const FENRIR_BOSS = bark("comms.fenrir.boss", [
  ["I AM IN YOUR CORE.", "ICH BIN IN DEINEM KERN."],
  ["BOSS THREAD ONLINE.", "BOSS-THREAD ONLINE."],
  ["NOW WATCH IT FALL.", "SIEH ZU, WIE ER FÄLLT."],
  ["THE BIG ONE IS ON YOU.", "DER GROSSE HOLT DICH."],
  ["KILL THIS — IF YOU CAN.", "TÖTE DIES — WENN DU KANNST."],
  ["THIS THREAD CHEWS GRIDS.", "DIESER THREAD FRISST GRIDS."],
  // S193 (BL-271): every boss wave (~9/run) — grown to match the cadence.
  ["MY CHAMPION TAKES THE FLOOR.", "MEIN CHAMPION BETRITT DAS FELD."],
  ["FEED IT YOUR TOWERS.", "FÜTTER ES MIT DEINEN TÜRMEN."],
  ["THIS ONE DOESN'T FLINCH.", "DER HIER ZUCKT NICHT."],
  ["STAND STILL. IT'S FASTER.", "BLEIB STEHEN. ES IST SCHNELLER."],
]);

export const VERA_BOSS_DOWN = bark("comms.vera.bossDown", [
  ["BOSS IS DOWN.", "BOSS IST ERLEDIGT."],
  ["THREAD TERMINATED.", "THREAD BEENDET."],
  ["DID WE JUST DO THAT?", "HABEN WIR DAS ECHT GESCHAFFT?"],
  ["BIG ONE — CLEAN.", "DER GROSSE — SAUBER."],
  ["THAT'S A KILL FOR THE LOG.", "DAS GEHT INS LOG."],
  ["GOOD HANDS. GOOD KILL.", "GUTE HÄNDE. GUTER TREFFER."],
  // S193 (BL-271): every boss wave (~10/run) — grown to match the cadence.
  ["HUGE SIGNATURE — GONE.", "RIESEN-SIGNATUR — WEG."],
  ["THE BIG THREAD SNAPPED.", "DER GROSSE THREAD IST GERISSEN."],
  ["THAT'S HOW BOSSES END.", "SO ENDEN BOSSE."],
  ["HEAVYWEIGHT DOWN. EXHALE.", "SCHWERGEWICHT LIEGT. AUSATMEN."],
]);

export const FENRIR_SNARL = bark("comms.fenrir.snarl", [
  ["...LUCKY.", "...GLÜCK GEHABT."],
  ["THIS ISN'T OVER.", "DAS IST NICHT VORBEI."],
  ["I'LL RIP IT BACK.", "ICH REISS ES ZURÜCK."],
  ["YOU STOLE ONE ROUND.", "DU HAST EINE RUNDE GESTOHLEN."],
  ["I WILL RECLAIM THIS.", "ICH HOLE ES MIR ZURÜCK."],
  ["ENJOY THE BREATH.", "GENIESS DEN ATEMZUG."],
  ["NOTHING IS HELD FOR LONG.", "NICHTS WIRD LANG GEHALTEN."],
  ["I REMEMBER THIS WAVE.", "ICH MERK MIR DIESE WELLE."],
  ["GLITCH IN MY FAVOR — SOON.", "GLITCH IN MEINEM SINNE — BALD."],
  // S193 (BL-271): fires after every boss down (~10/run) — grown to match.
  ["A SCRATCH. NOTHING MORE.", "EIN KRATZER. MEHR NICHT."],
  ["I HAVE MORE WHERE THAT LIVED.", "DAVON HAB ICH NOCH MEHR."],
  ["SAVOR IT. IT'S RARE.", "GENIESS ES. IST SELTEN."],
]);

// ── S193 (BL-272) — tone/phase-routed praise sub-pools ────────────────────────
// The milestone praise is no longer trajectory-blind: the director routes it
// through CommsMomentum's RivalryTone (the FENRIR-gloat pattern generalized)
// and gives the endgame (W30+) its own gravity. Same beat, smarter voice.

// Player dominating (tone "rattled" — FENRIR off-balance): quiet superiority.
export const VERA_WAVE_PRAISE_DOMINANT = bark("comms.vera.wavePraiseDominant", [
  ["SHE CAN'T FIND A GAP.", "SIE FINDET KEINE LÜCKE."],
  ["ANOTHER WAVE, ANOTHER WALL.", "NOCH EINE WELLE, NOCH EINE WAND."],
  ["THE GRID BARELY NOTICED.", "DAS GRID HAT ES KAUM GEMERKT."],
  ["WE'RE MAKING THIS LOOK EASY.", "WIR LASSEN DAS LEICHT AUSSEHEN."],
  ["CLINICAL. KEEP IT BORING.", "PRÄZISE. HALT ES LANGWEILIG."],
  ["THE WOLF IS QUIET. GOOD SIGN.", "DER WOLF IST STILL. GUTES ZEICHEN."],
]);

// Player hurting but the wave still cleared (tone "ascendant"): triage relief.
export const VERA_WAVE_PRAISE_RELIEF = bark("comms.vera.wavePraiseRelief", [
  ["HELD. BARELY — BUT HELD.", "GEHALTEN. KNAPP — ABER GEHALTEN."],
  ["WE NEEDED THAT ONE.", "DIE HABEN WIR GEBRAUCHT."],
  ["STILL STANDING. USE THE PAUSE.", "WIR STEHEN NOCH. NUTZ DIE PAUSE."],
  ["ROUGH — BUT THE LINE STANDS.", "HART — ABER DIE LINIE STEHT."],
  ["BREATHE. REPAIR. CONTINUE.", "ATMEN. FLICKEN. WEITER."],
  ["PATCH UP WHILE IT'S QUIET.", "FLICK ALLES, SOLANGE RUHE IST."],
]);

// The endgame (W30+): the same praise beat carries finale gravity.
export const VERA_WAVE_PRAISE_FINALE = bark("comms.vera.wavePraiseFinale", [
  ["CLEAN — THIS DEEP IN. RESPECT.", "SAUBER — SO TIEF IM RUN. RESPEKT."],
  ["THE LOG IS WATCHING THESE WAVES.", "DAS LOG SIEHT DIESE WELLEN."],
  ["ENDGAME WAVE. HANDLED.", "ENDGAME-WELLE. ERLEDIGT."],
  ["FEW GRIDS EVER SEE THIS FAR.", "KAUM EIN GRID KOMMT SO WEIT."],
  ["HOLD THIS FORM TO THE GATE.", "HALT DIESE FORM BIS ZUM TOR."],
]);

export const VERA_WAVE_PRAISE = bark("comms.vera.wavePraise", [
  ["CLEAN WAVE.", "SAUBERE WELLE."],
  ["THAT WAS SHARP.", "DAS WAR SCHARF."],
  ["NICE CONTROL.", "GUTE KONTROLLE."],
  ["GOOD DEFENSE CYCLE.", "GUTER ABWEHRZYKLUS."],
  ["TIGHT EXECUTION.", "PRÄZISE AUSGEFÜHRT."],
  ["WAVE CONTAINED.", "WELLE EINGEDÄMMT."],
  ["TEXTBOOK HOLD.", "WIE AUS DEM LEHRBUCH."],
  ["GRID READS GREEN.", "GRID ZEIGT GRÜN."],
  ["PROCESS CLEAR. NICE.", "PROZESS SAUBER. GUT."],
  ["THAT'S HOW WE DO IT.", "GENAU SO MACHEN WIR DAS."],
]);

export const VERA_BARRAGE = bark("comms.vera.barrage", [
  ["CORE UNDER BARRAGE!", "KERN UNTER DAUERFEUER!"],
  ["TOO MANY CORE HITS!", "ZU VIELE KERNTREFFER!"],
  ["CORE PANIC WINDOW!", "KERN-PANIKFENSTER!"],
  ["BARRAGE INBOUND!", "SPERRFEUER IM ANFLUG!"],
  ["CORE BUFFER COLLAPSING!", "KERN-PUFFER BRICHT ZUSAMMEN!"],
  ["HITS STACKING — STOP IT!", "TREFFER STAPELN SICH — STOPP!"],
  ["WE CAN'T EAT THIS RATE!", "DIESE RATE SCHAFFEN WIR NICHT!"],
  ["MAINFRAME OVERLOADING!", "MAINFRAME ÜBERLASTET!"],
]);

export const FENRIR_SPAWN = bark("comms.fenrir.spawn", [
  ["NEW LANE, MY RULES.", "NEUE BAHN, MEINE REGELN."],
  ["ANOTHER ENTRY OPENS.", "NOCH EIN ZUGANG ÖFFNET SICH."],
  ["MORE THREADS. MORE FUN.", "MEHR THREADS. MEHR SPASS."],
  ["I SPLIT YOUR DEFENSE.", "ICH SPALTE DEINE ABWEHR."],
  ["WATCH THIS BREACH BLOOM.", "SIEH DIESEN BRUCH ERBLÜHEN."],
  ["YOUR FLANK IS MINE.", "DEINE FLANKE GEHÖRT MIR."],
  ["TWO FRONTS NOW. ONE OF YOU.", "ZWEI FRONTEN. EINER VON DIR."],
  ["DOORS OPEN WHERE I WANT.", "TÜREN ÖFFNEN, WO ICH WILL."],
]);

export const VERA_SPAWN = bark("comms.vera.spawn", [
  ["NEW SPAWN ONLINE!", "NEUER SPAWN ONLINE!"],
  ["SECOND VECTOR LIVE!", "ZWEITER VEKTOR LIVE!"],
  ["SPAWN LANE ACTIVE!", "SPAWN-BAHN AKTIV!"],
  ["NEW BREACH FRONT!", "NEUE BRUCHFRONT!"],
  ["WE'RE SPLIT. ADAPT!", "WIR SIND GETEILT. ANPASSEN!"],
  ["FLANK CONTACT — REACT!", "FLANKE — REAGIER!"],
  ["ANOTHER DOOR. COVER IT.", "NOCH EINE TÜR. DECK SIE."],
  ["GRID GREW — TIGHTEN UP.", "GRID GRÖSSER — ENGER ZIEHEN."],
]);

export const VERA_SWARM = bark("comms.vera.swarm", [
  ["SWARM PRESSURE RISING!", "SCHWARMDRUCK STEIGT!"],
  ["GRID CROWDING FAST!", "GRID FÜLLT SICH SCHNELL!"],
  ["WE'RE GETTING FLOODED!", "WIR WERDEN ÜBERSCHWEMMT!"],
  ["CONTACTS STACKING UP!", "KONTAKTE STAPELN SICH!"],
  ["SWARM THRESHOLD HIT!", "SCHWARM-SCHWELLE ERREICHT!"],
  ["TOO MANY THREADS!", "ZU VIELE THREADS!"],
  ["BOARD IS PACKED — CULL!", "BOARD VOLL — AUSDÜNNEN!"],
  ["SWARM IS LOUD — STAY CALM.", "SCHWARM IST LAUT — RUHE BEWAHREN."],
]);

export const VERA_UPGRADE_TIP = bark("comms.vera.upgradeTip", [
  ["UPGRADE YOUR TOWERS — CLICK ONE!", "RÜSTE DEINE TÜRME AUF — KLICK EINEN!"],
  ["TIME TO UPGRADE. CLICK A TOWER!", "ZEIT ZUM AUFRÜSTEN. KLICK EINEN TURM!"],
  [
    "BOOST A NODE — CLICK TO UPGRADE!",
    "KNOTEN STÄRKEN — KLICK ZUM AUFRÜSTEN!",
  ],
  ["FEED A TOWER — IT'S CHEAP.", "FÜTTER EINEN TURM — IST GÜNSTIG."],
  ["A TOWER UP IS A WAVE BANKED.", "EIN TURM HOCH IST EINE WELLE GESPART."],
]);

// Rivalry-tone variant: FENRIR pressing a winning position (player bleeding).
export const FENRIR_GLOAT_ASCENDANT = bark("comms.fenrir.gloatAscendant", [
  ["YOUR WALL IS CRACKING.", "DEINE MAUER BRICHT."],
  ["I CAN TASTE THE CORE.", "ICH SCHMECKE DEN KERN."],
  ["CLOSER EVERY WAVE.", "NÄHER MIT JEDER WELLE."],
  ["YOU'RE RUNNING OUT OF GRID.", "DIR GEHT DAS GRID AUS."],
  ["THIS IS HOW IT ENDS.", "SO ENDET ES."],
  ["FEEL THE FLOOR TILT.", "SPÜR, WIE DER BODEN KIPPT."],
  ["YOUR TOWERS ARE GUESSING NOW.", "DEINE TÜRME RATEN JETZT NUR NOCH."],
  ["ONE GOOD WAVE FROM ME.", "EINE GUTE WELLE NOCH VON MIR."],
]);

export const VERA_FIRST_BLOOD = bark("comms.vera.firstBlood", [
  ["FIRST KILL. STAY SHARP.", "ERSTER KILL. BLEIB WACH."],
  ["ONE DOWN. HOLD THE LINE.", "EINER WEG. HALT DIE LINIE."],
  ["CONTACT DOWN. GOOD START.", "KONTAKT ERLEDIGT. GUTER START."],
  ["THAT'S ONE. KEEP CUTTING.", "DAS WAR EINER. WEITER SO."],
  ["EDGE IS GOOD. KEEP IT.", "DIE KLINGE SITZT. WEITER."],
  ["TARGETS DROP — WE WORK.", "ZIELE FALLEN — WIR ARBEITEN."],
  ["FIRST OF MANY.", "ERSTER VON VIELEN."],
]);

export const VERA_FLAWLESS = bark("comms.vera.flawless", [
  ["NOT A SCRATCH. KEEP IT.", "KEIN KRATZER. SO BLEIBT DAS."],
  ["FLAWLESS LINE. KEEP IT SEALED.", "MAKELLOSE LINIE. DICHT HALTEN."],
  ["THEY CAN'T TOUCH US.", "SIE KRIEGEN UNS NICHT."],
  ["THIS IS WHAT CONTROL LOOKS LIKE.", "SO SIEHT KONTROLLE AUS."],
  ["AIRTIGHT. STAY ON IT.", "WASSERDICHT. DRANBLEIBEN."],
  ["SHE WATCHES — AND CAN'T REACH.", "SIE SCHAUT — UND KOMMT NICHT RAN."],
  ["THE LOG LIKES THIS RUN.", "DAS LOG MAG DIESEN RUN."],
  ["KEEP THIS QUIET. STAY GOLD.", "BLEIB LEISE. BLEIB MAKELLOS."],
]);

// FENRIR off-balance — grudging respect, never panicked. Stays cold and cool.
export const FENRIR_RATTLED = bark("comms.fenrir.rattled", [
  ["...HOW ARE YOU THIS CLEAN.", "...WIE BIST DU SO SAUBER."],
  ["YOUR WALL HOLDS. FOR NOW.", "DEINE MAUER HÄLT. VORERST."],
  ["I UNDERESTIMATED THIS GRID.", "DIESES GRID — UNTERSCHÄTZT."],
  ["ENJOY THE STREAK. IT ENDS.", "GENIESS DIE SERIE. SIE ENDET."],
  ["DON'T GET COMFORTABLE.", "WERD NICHT BEQUEM."],
  ["YOU LEARN. SO WILL I.", "DU LERNST. ICH AUCH."],
  ["A QUIET WAVE IS A LOAN.", "EINE STILLE WELLE IST GELIEHEN."],
  ["NOTED. NEXT TIME — DIFFERENT.", "NOTIERT. NÄCHSTES MAL — ANDERS."],
  // S193 (BL-271): this pool also carries the rattled boss-down concession
  // now (~15 fires in a dominated run, measured) — grown to match.
  ["YOUR GRID LEARNS FAST. TOO FAST.", "DEIN GRID LERNT SCHNELL. ZU SCHNELL."],
  ["I'LL FIND THE SEAM. THERE'S ALWAYS ONE.", "ICH FINDE DIE NAHT. ES GIBT IMMER EINE."],
  ["HOLD YOUR TROPHIES LOOSELY.", "HALT DEINE TROPHÄEN NICHT ZU FEST."],
  ["EVEN WALLS GET TIRED.", "AUCH MAUERN WERDEN MÜDE."],
]);

export const VERA_COMEBACK = bark("comms.vera.comeback", [
  ["WE CLAWED BACK.", "WIR HABEN UNS ZURÜCKGEKÄMPFT."],
  ["STABILIZED. BREATHE.", "STABILISIERT. DURCHATMEN."],
  ["OFF THE BRINK. GOOD HANDS.", "WEG VOM ABGRUND. GUTE ARBEIT."],
  ["WE HELD WHEN IT COUNTED.", "WIR HIELTEN, ALS ES ZÄHLTE."],
  ["BACK FROM RED. NICE.", "ZURÜCK AUS DEM ROT. GUT."],
  ["WE EARNED THAT BREATH.", "DEN ATEMZUG HABEN WIR VERDIENT."],
  ["THE LINE BENT — DIDN'T BREAK.", "DIE LINIE BOG SICH — BLIEB GANZ."],
]);

export const FENRIR_COMEBACK = bark("comms.fenrir.comeback", [
  ["YOU SHOULD BE DEAD.", "DU SOLLTEST TOT SEIN."],
  ["THE BRINK SUITS YOU.", "DER ABGRUND STEHT DIR."],
  ["I HAD YOU. I'LL HAVE YOU AGAIN.", "ICH HATTE DICH. ICH KRIEG DICH WIEDER."],
  ["BORROWED TIME. NOTHING MORE.", "GELIEHENE ZEIT. MEHR NICHT."],
  ["THE WALL WAS GLASS. STILL IS.", "DIE MAUER WAR GLAS. IST SIE NOCH."],
  ["YOU CRAWL. I WAIT.", "DU KRIECHST. ICH WARTE."],
  ["RESPIRATOR. NOT A SHIELD.", "BEATMUNG. KEIN SCHILD."],
]);

export const VERA_GAME_OVER = bark("comms.vera.gameOver", [
  ["...I'M SORRY.", "...ES TUT MIR LEID."],
  ["WE LOST THE CORE.", "DER KERN IST VERLOREN."],
  ["LINK DROPPING.", "VERBINDUNG BRICHT AB."],
  ["...IT'S DARK HERE.", "...ES IST DUNKEL HIER."],
  ["THEY HAVE THE MAINFRAME.", "SIE HABEN DEN MAINFRAME."],
]);

// Exclusive reaction to deploying the KERNEL PANIC payload (the gold legendary).
// VERA's glasses literally fly off — pure awe. Gold variant ONLY (memory_leak
// stays quiet here).
export const VERA_KERNEL_PANIC = bark("comms.vera.kernelPanic", [
  ["WHOA — KERNEL PANIC!", "WOAH — KERNEL PANIC!"],
  ["YOU DEPLOYED THE BIG ONE!", "DU HAST DAS GROSSE GEZÜNDET!"],
  ["ROOT-LEVEL CHAOS. YES!", "CHAOS AUF ROOT-EBENE. JA!"],
  ["DID YOU JUST—?! INSANE.", "HAST DU GERADE—?! WAHNSINN."],
  ["SYSTEM-WIDE PANIC. GO!", "SYSTEMWEITE PANIK. LOS!"],
  ["PANIC PUSHED. HOLD ON!", "PANIC AUSGELÖST. HALT DICH FEST!"],
  ["OH MY GOD — THE NUKE!", "OH MEIN GOTT — DIE NUKE!"],
  ["I LOVE WHEN YOU DO THAT.", "ICH LIEBE ES, WENN DU DAS MACHST."],
]);

// Everyday happy bark when an ORDINARY (non-gold) payload is deployed — e.g. the
// violet Memory Leak (reduceMaxHp). VERA grins (cheer pose) and the frame stays
// green: this is the "nice shot" nod, NOT the exclusive gold KERNEL PANIC awe.
export const VERA_PAYLOAD_LAUNCH = bark("comms.vera.payloadLaunch", [
  ["PAYLOAD AWAY!", "PAYLOAD RAUS!"],
  ["MEMORY LEAK DEPLOYED!", "SPEICHERLECK AUSGELÖST!"],
  ["THAT'LL SOFTEN THEM UP!", "DAS WEICHT SIE AUF!"],
  ["WARHEAD'S LIVE — NICE!", "SPRENGKOPF SCHARF — STARK!"],
  ["LEAK'S IN. GOOD HIT!", "LECK SITZT. GUTER TREFFER!"],
  ["PAYLOAD OUT. SWEET!", "PAYLOAD AB. SAUBER!"],
  ["NICE TIMING ON THAT.", "GUTES TIMING."],
  ["SAVE THE NEXT ONE TOO.", "HEB DEN NÄCHSTEN AUCH AUF."],
]);

// First shielded wave — FENRIR gloats his pack now wears armor.
export const FENRIR_SHIELD_FIRST = bark("comms.fenrir.shieldFirst", [
  ["MY PACK WEARS ARMOR NOW.", "MEIN RUDEL TRÄGT JETZT PANZER."],
  ["SHIELDS UP. CHEW ON THAT.", "SCHILDE OBEN. SCHLUCK DAS."],
  ["ARMORED AND HUNGRY.", "GEPANZERT UND HUNGRIG."],
  ["GOOD LUCK PEELING THESE.", "VIEL GLÜCK BEIM SCHÄLEN."],
  ["HARDENED. YOU'RE NOT.", "GEHÄRTET. DU NICHT."],
  ["NEW PLATING. NEW PROBLEM.", "NEUE PANZERUNG. NEUES PROBLEM."],
  ["FEELS DIFFERENT NOW, DOESN'T IT.", "FÜHLT SICH ANDERS AN, NICHT?"],
]);

// VERA's defiant response — hints at the EMP counter.
export const VERA_SHIELD_FIRST = bark("comms.vera.shieldFirst", [
  ["SHIELDS? WE'LL BREAK THEM.", "SCHILDE? WIR BRECHEN SIE."],
  ["ARMOR CRACKS — HIT HARDER.", "PANZER BRICHT — SCHLAG HÄRTER."],
  ["BRING THE EMP. WE CRACK IT.", "HER MIT DEM EMP. WIR KNACKEN ES."],
  ["NEW PLATING, SAME OUTCOME.", "NEUE PANZERUNG, GLEICHES ENDE."],
  ["WE PUNCH THROUGH. WATCH.", "WIR SCHLAGEN DURCH. SIEH ZU."],
  ["ELECTRIC TOWERS LOVE METAL.", "ELEKTROTÜRME LIEBEN METALL."],
  ["EVERY SHIELD HAS A SEAM.", "JEDER SCHILD HAT EINE NAHT."],
]);

// ── S193 (BL-272) — the victory beat: the rivalry resolves ON AIR ─────────────
// game.won had no comms voice at all; the wolf never conceded on his own
// channel. VERA signs off, then FENRIR answers with the `defeated` face
// (paid-for art that never aired in live comms — BL-274).
export const VERA_VICTORY = bark("comms.vera.victory", [
  ["THE LINE HELD. WE'RE DONE.", "DIE LINIE STEHT. GESCHAFFT."],
  ["GRID SECURE. ALL OF IT.", "GRID GESICHERT. KOMPLETT."],
  ["THE LOG GETS A GOOD PAGE TODAY.", "DAS LOG KRIEGT HEUTE EINE GUTE SEITE."],
  ["SHE'S OUT. WE'RE STILL HERE.", "SIE IST RAUS. WIR SIND NOCH DA."],
  ["MAINFRAME STANDS. WELL FOUGHT.", "DER MAINFRAME STEHT. STARK GEKÄMPFT."],
]);

// Callback variant (S193 memory beat): the run brushed death and still won.
export const VERA_VICTORY_HARDWON = bark("comms.vera.victoryHardwon", [
  ["FROM THE BRINK TO THE TOP.", "VOM ABGRUND GANZ NACH OBEN."],
  ["WE ALMOST LOST IT. WE DIDN'T.", "FAST VERLOREN. ABER NUR FAST."],
  ["SCARS AND ALL — WE HOLD.", "MIT NARBEN — ABER WIR HALTEN."],
  ["THAT COMEBACK GOES IN THE LOG.", "DAS COMEBACK GEHT INS LOG."],
  ["YOU BENT. YOU NEVER BROKE.", "DU HAST DICH GEBOGEN. NIE GEBROCHEN."],
]);

// FENRIR's on-air concession — the `defeated` expression's live-comms home.
export const FENRIR_PURGED = bark("comms.fenrir.purged", [
  ["...PURGED. THIS TIME.", "...GELÖSCHT. DIESES MAL."],
  ["THE GRID KEEPS ITS GUARD.", "DAS GRID BEHÄLT SEINEN WÄCHTER."],
  ["I REMEMBER EVERY TOWER.", "ICH MERKE MIR JEDEN TURM."],
  ["ENJOY IT. WOLVES COME BACK.", "GENIESS ES. WÖLFE KOMMEN WIEDER."],
  ["...WELL HELD, OPERATOR.", "...GUT GEHALTEN, OPERATOR."],
]);

// ── S193 (BL-272) — the endless door (W36, Echo Mode) gets a voice ────────────
export const VERA_ENDLESS = bark("comms.vera.endless", [
  ["BEYOND THE LAST CHART NOW.", "AB HIER GIBT ES KEINE KARTE MEHR."],
  ["THE LOG HAS NO PAGE FOR THIS.", "DAFÜR HAT DAS LOG KEINE SEITE."],
  ["UNCHARTED WAVES. STAY SHARP.", "UNKARTIERTE WELLEN. BLEIB WACH."],
  ["PAST THE GATE. STILL HOLDING.", "HINTERM TOR. WIR HALTEN WEITER."],
  ["ECHO PROTOCOL LIVE. GO.", "ECHO-PROTOKOLL LIVE. LOS."],
]);

// FENRIR's answer at the endless door — the `taunting` face on air (BL-274).
export const FENRIR_ENDLESS_TAUNT = bark("comms.fenrir.endlessTaunt", [
  ["STILL HUNGRY? GOOD. SO AM I.", "NOCH HUNGRIG? GUT. ICH AUCH."],
  ["NO MAP OUT HERE, LITTLE GUARD.", "HIER DRAUSSEN GIBT'S KEINE KARTE."],
  ["THE DEEP WAVES ARE MINE.", "DIE TIEFEN WELLEN GEHÖREN MIR."],
  ["LET'S SEE WHERE YOU CRACK.", "MAL SEHEN, WO DU BRICHST."],
  ["FOREVER SUITS ME FINE.", "FÜR IMMER PASST MIR GUT."],
]);

// ── S193 (BL-272) — the once-per-act press-taunt: mid-run rivalry when the
// wolf smells blood (tone "ascendant"), on the `taunting` face. Fills the
// measured dead-air between boss waves without new spam (minor-cooldown-gated,
// once per act).
export const FENRIR_PRESS_TAUNT = bark("comms.fenrir.pressTaunt", [
  ["HEAR THAT? YOUR GRID CREAKS.", "HÖRST DU? DEIN GRID KNARRT."],
  ["I'M NOT EVEN HUNTING YET.", "ICH JAGE NOCH NICHT MAL RICHTIG."],
  ["YOUR REPAIRS BORE ME. FALL.", "DEINE REPARATUREN LANGWEILEN MICH."],
  ["SMELL THAT? THAT'S THE END.", "RIECHST DU DAS? DAS IST DAS ENDE."],
  ["KEEP PATCHING. I KEEP CHEWING.", "FLICK NUR WEITER. ICH BEISSE WEITER."],
  ["THE PACK LIKES A SLOW MEAL.", "DAS RUDEL MAG LANGSAME MAHLZEITEN."],
]);

// ── S194 (BL-277 M1) — the near-miss save: a kill at the core's door ─────────
// Tone-routed speaker: VERA's relief normally, FENRIR's mock when he presses.
export const VERA_NEAR_MISS = bark("comms.vera.nearMiss", [
  ["THAT ONE TOUCHED THE WIRE.", "DER WAR SCHON AM DRAHT."],
  ["CAUGHT AT THE DOOR. BREATHE.", "AN DER TÜR GESTOPPT. ATMEN."],
  ["TOO CLOSE. TIGHTEN THE EXIT.", "ZU KNAPP. ZIEH DEN AUSGANG ZU."],
  ["LAST-METER KILL. GOOD EYES.", "LETZTER METER. GUTE AUGEN."],
  ["THE CORE FELT THE WIND.", "DER KERN HAT DEN LUFTZUG GESPÜRT."],
  ["SAVED AT THE THRESHOLD.", "AN DER SCHWELLE GERETTET."],
]);

export const FENRIR_NEAR_MISS = bark("comms.fenrir.nearMiss", [
  ["LUCK. NOTHING MORE.", "GLÜCK. MEHR NICHT."],
  ["ONE STEP SHORT. NEXT TIME.", "EINEN SCHRITT ZU KURZ. NÄCHSTES MAL."],
  ["I ALMOST HAD THE DOOR.", "ICH HATTE DIE TÜR FAST."],
  ["YOUR HANDS SHOOK. I SAW IT.", "DEINE HÄNDE ZITTERTEN. ICH SAH ES."],
  ["THE NEXT ONE WALKS THROUGH.", "DER NÄCHSTE GEHT DURCH."],
  ["CLOSE ONLY COUNTS FOR ME.", "KNAPP ZÄHLT NUR FÜR MICH."],
]);

// ── S194 (BL-277 M2) — economy intelligence: the D-236 surplus cap, TAUGHT ───
// Banked gold above the surplus threshold softens the wave bonus (Act II+);
// nothing in the game ever explained it. VERA teaches; FENRIR mocks the hoard.
export const VERA_SURPLUS_TIP = bark("comms.vera.surplusTip", [
  ["IDLE CREDITS SHRINK THE BONUS.", "RUHENDE CREDITS SCHRUMPFEN DEN BONUS."],
  ["BANKED GOLD EARNS NOTHING. BUILD.", "GEPARKTES GOLD BRINGT NICHTS. BAU."],
  ["THE GRID PAYS SPENDERS. INVEST.", "DAS GRID BELOHNT INVESTOREN. BAU AUS."],
  ["THAT VAULT COULD BE TOWERS.", "DER TRESOR KÖNNTE TÜRME SEIN."],
  ["REINVEST — THE BONUS IS FADING.", "REINVESTIEREN — DER BONUS SCHMILZT."],
]);

export const FENRIR_HOARD_MOCK = bark("comms.fenrir.hoardMock", [
  ["A DRAGON'S HOARD GUARDS NOTHING.", "EIN DRACHENSCHATZ SCHÜTZT NICHTS."],
  ["COUNT YOUR COINS. I'LL WAIT.", "ZÄHL DEINE MÜNZEN. ICH WARTE."],
  ["RICH GRIDS FALL SOFTEST.", "REICHE GRIDS FALLEN AM WEICHSTEN."],
  ["YOUR VAULT WON'T BITE ME.", "DEIN TRESOR BEISST MICH NICHT."],
  ["GOLD IS NOT A WALL.", "GOLD IST KEINE MAUER."],
]);

// ── S194 (BL-277 M3) — the dead-air killer: quiet-gated ambient exchanges ────
// When the feed has been silent ≥30 s mid-wave, the broadcast breathes: the
// wolf probes while you dominate (smug needle → VERA's cool parry), the desk
// hums when things are even. The ascendant side reuses FENRIR_PRESS_TAUNT.
export const FENRIR_NEEDLE = bark("comms.fenrir.needle", [
  ["STILL AWAKE OVER THERE?", "NOCH WACH DA DRÜBEN?"],
  ["DON'T MISTAKE QUIET FOR PEACE.", "VERWECHSLE STILLE NICHT MIT FRIEDEN."],
  ["I'M STUDYING YOUR PATTERNS.", "ICH STUDIERE DEINE MUSTER."],
  ["EVERY WALL HAS A HABIT.", "JEDE MAUER HAT EINE GEWOHNHEIT."],
  ["THE PACK IS PATIENT TODAY.", "DAS RUDEL IST GEDULDIG HEUTE."],
  ["ENJOY THE LULL. IT'S MINE TOO.", "GENIESS DIE FLAUTE. SIE IST AUCH MEINE."],
]);

export const VERA_PARRY = bark("comms.vera.parry", [
  ["HE'S FISHING. IGNORE IT.", "ER FISCHT NUR. IGNORIER ES."],
  ["LET HIM TALK. WE WORK.", "LASS IHN REDEN. WIR ARBEITEN."],
  ["NOISE ON CH-66. AS USUAL.", "RAUSCHEN AUF CH-66. WIE IMMER."],
  ["HIS PATIENCE. OUR ADVANTAGE.", "SEINE GEDULD. UNSER VORTEIL."],
  ["KEEP BUILDING. HE HATES THAT.", "BAU WEITER. DAS HASST ER."],
]);

export const VERA_OPS_CHATTER = bark("comms.vera.opsChatter", [
  ["RUNNING DIAGNOSTICS. ALL GREEN.", "DIAGNOSE LÄUFT. ALLES GRÜN."],
  ["REROUTING SPARE CYCLES.", "FREIE ZYKLEN WERDEN UMGELEITET."],
  ["FEED IS CLEAN. STAY READY.", "FEED IST SAUBER. BLEIB BEREIT."],
  ["QUIET SHIFT. I'LL TAKE IT.", "RUHIGE SCHICHT. NEHM ICH GERN."],
  ["LOG SYNCED. LINE STEADY.", "LOG SYNCHRON. LINIE STABIL."],
  ["COOLING THE RELAYS WHILE WE CAN.", "KÜHLE DIE RELAIS, SOLANGE ES GEHT."],
]);

/** EN/DE corpus for the comms barks (BL-89). EN is the canonical fallback;
 *  `translations.ts` registers both into the i18n seam, `commsLine` resolves. */
export { COMMS_EN, COMMS_DE };

export const BEAT = {
  runStart: {
    id: "run-start",
    priority: PRIORITY.LOW,
    minor: true,
    speaker: "vera",
  } as BeatSpec,
  stealthFirst: {
    id: "stealth-first",
    priority: PRIORITY.MED,
    minor: true,
    speaker: "vera",
  } as BeatSpec,
  stealthRepeat: {
    id: "stealth-repeat",
    priority: PRIORITY.LOW,
    minor: true,
    speaker: "vera",
  } as BeatSpec,
  coreHit: {
    id: "core-hit",
    priority: PRIORITY.MED,
    minor: true,
    speaker: "fenrir",
  } as BeatSpec,
  coreHeavyHit: {
    id: "core-heavy-hit",
    priority: PRIORITY.HIGH,
    minor: false,
    speaker: "vera",
  } as BeatSpec,
  coreCritical: {
    id: "core-critical",
    priority: PRIORITY.HIGH,
    minor: false,
    speaker: "vera",
  } as BeatSpec,
  bossIncoming: {
    id: "boss-incoming",
    priority: PRIORITY.HIGH,
    minor: false,
    speaker: "fenrir",
  } as BeatSpec,
  bossDown: {
    id: "boss-down",
    priority: PRIORITY.HIGH,
    minor: false,
    speaker: "vera",
  } as BeatSpec,
  coreBarrage: {
    id: "core-barrage",
    priority: PRIORITY.HIGH,
    minor: false,
    speaker: "vera",
  } as BeatSpec,
  spawnBreach: {
    id: "spawn-breach",
    priority: PRIORITY.HIGH,
    minor: false,
    speaker: "fenrir",
  } as BeatSpec,
  swarmPressure: {
    id: "swarm-pressure",
    priority: PRIORITY.MED,
    minor: true,
    speaker: "vera",
  } as BeatSpec,
  firstLeak: {
    id: "first-leak",
    priority: PRIORITY.MED,
    minor: false,
    speaker: "vera",
  } as BeatSpec,
  nodeLost: {
    id: "node-lost",
    priority: PRIORITY.MED,
    minor: true,
    speaker: "vera",
  } as BeatSpec,
  newSignature: {
    id: "new-signature",
    priority: PRIORITY.LOW,
    minor: true,
    speaker: "vera",
  } as BeatSpec,
  upgradeTip: {
    id: "upgrade-tip",
    priority: PRIORITY.LOW,
    minor: true,
    speaker: "vera",
  } as BeatSpec,
  wavePraise: {
    id: "wave-praise",
    priority: PRIORITY.MED,
    minor: true,
    speaker: "vera",
  } as BeatSpec,
  firstBlood: {
    id: "first-blood",
    priority: PRIORITY.LOW,
    minor: false,
    speaker: "vera",
  } as BeatSpec,
  flawlessStreak: {
    id: "flawless-streak",
    priority: PRIORITY.MED,
    minor: true,
    speaker: "vera",
  } as BeatSpec,
  comeback: {
    id: "comeback",
    priority: PRIORITY.HIGH,
    minor: false,
    speaker: "vera",
  } as BeatSpec,
  gameOver: {
    id: "game-over",
    priority: PRIORITY.MAX,
    minor: false,
    speaker: "vera",
  } as BeatSpec,
  // Player-initiated, rare (legendary, max-3 rack) → HIGH so the WOW lands, but
  // below MAX so a game-over still wins.
  kernelPanic: {
    id: "kernel-panic",
    priority: PRIORITY.HIGH,
    minor: false,
    speaker: "vera",
  } as BeatSpec,
  // Ordinary (non-gold) payload deploy → modest happy cheer, frame stays green.
  // MED + non-minor: reliably acknowledges the deploy, but never talks over a
  // HIGH/MAX beat (boss, core-critical, the gold KERNEL PANIC WOW).
  payloadLaunch: {
    id: "payload-launch",
    priority: PRIORITY.MED,
    minor: false,
    speaker: "vera",
  } as BeatSpec,
  // First time shields appear this run — FENRIR gloats his pack is armored,
  // VERA answers defiant. HIGH so it lands; below MAX so game-over still wins.
  shieldFirst: {
    id: "shield-first",
    priority: PRIORITY.HIGH,
    minor: false,
    speaker: "fenrir",
  } as BeatSpec,
  // S193 (BL-272): the run is WON — the rivalry resolves on air. MAX so the
  // sign-off can never be talked over (there is nothing above winning).
  victory: {
    id: "victory",
    priority: PRIORITY.MAX,
    minor: false,
    speaker: "vera",
  } as BeatSpec,
  // S193 (BL-272): the endless door (W36) — once per run, HIGH so it lands
  // over the wave-start bustle, below MAX so a game-over still wins.
  endlessEntry: {
    id: "endless-entry",
    priority: PRIORITY.HIGH,
    minor: false,
    speaker: "vera",
  } as BeatSpec,
  // S193 (BL-272): the once-per-act ambient press-taunt — deliberately LOW +
  // minor so it only fills genuine quiet and never talks over anything.
  pressTaunt: {
    id: "press-taunt",
    priority: PRIORITY.LOW,
    minor: true,
    speaker: "fenrir",
  } as BeatSpec,
  // S194 (BL-277 M1): a save at the core's door — worth a remark, but modest
  // (MED, minor) and rate-limited by the director (min 3-wave spacing).
  nearMiss: {
    id: "near-miss",
    priority: PRIORITY.MED,
    minor: true,
    speaker: "vera",
  } as BeatSpec,
  nearMissMock: {
    id: "near-miss-mock",
    priority: PRIORITY.MED,
    minor: true,
    speaker: "fenrir",
  } as BeatSpec,
  // S194 (BL-277 M2): the surplus teaching — the upgradeTip register (LOW,
  // minor, once per run, consumed only when it actually plays).
  surplusTip: {
    id: "surplus-tip",
    priority: PRIORITY.LOW,
    minor: true,
    speaker: "vera",
  } as BeatSpec,
  hoardMock: {
    id: "hoard-mock",
    priority: PRIORITY.LOW,
    minor: true,
    speaker: "fenrir",
  } as BeatSpec,
  // S194 (BL-277 M3): the quiet-gated ambient beats — LOW + minor by LAW: they
  // exist to fill genuine dead air and must never talk over anything.
  ambientOps: {
    id: "ambient-ops",
    priority: PRIORITY.LOW,
    minor: true,
    speaker: "vera",
  } as BeatSpec,
  ambientNeedle: {
    id: "ambient-needle",
    priority: PRIORITY.LOW,
    minor: true,
    speaker: "fenrir",
  } as BeatSpec,
};
